/**
 * SettlementEditor — receive (debtor) or pay (creditor) money and choose
 * WHICH bills it settles (Tally "Agst Ref"). Also edits an existing
 * receipt/payment voucher: amount, date, mode, narration and re-allocation,
 * and lets an on-account advance be allocated to bills later.
 *
 * Backend: /api/settlements (open-bills · POST · GET/PUT/DELETE {voucher_id})
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Wand2, Eraser, Save, Wallet, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const METHODS = ['NEFT', 'UPI', 'Cash', 'Cheque', 'RTGS', 'IMPS', 'Bank'];
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };

/** Pure allocation grid — shared by the editor and the Vouchers form. */
const THEMES = {
  dark: {
    hint: 'text-zinc-400', box: 'border-zinc-800', head: 'bg-zinc-900 text-zinc-500', divide: 'divide-zinc-800/60',
    hover: 'hover:bg-zinc-800/30', ref: 'text-amber-300', refOpening: 'text-purple-300', sub: 'text-zinc-500', mono: 'text-zinc-300',
    due: 'text-red-300', input: 'bg-zinc-800 text-zinc-50 border-zinc-700 focus:border-emerald-500', over: 'border-red-500',
    card: 'bg-zinc-900', cardLabel: 'text-zinc-500', cardVal: 'text-zinc-100', empty: 'text-zinc-500 border-zinc-700',
    btnA: 'bg-amber-500/15 text-amber-300 border-amber-600/40 hover:bg-amber-500/25', btnB: 'bg-zinc-800 text-zinc-300 border-zinc-700 hover:bg-zinc-700',
    picked: 'bg-emerald-500/5',
  },
  light: {
    hint: 'text-gray-600', box: 'border-gray-300', head: 'bg-blue-50 text-gray-600', divide: 'divide-gray-200',
    hover: 'hover:bg-blue-50/60', ref: 'text-blue-900', refOpening: 'text-purple-700', sub: 'text-gray-500', mono: 'text-gray-800',
    due: 'text-red-700', input: 'bg-white text-gray-900 border-gray-300 focus:border-blue-500', over: 'border-red-500',
    card: 'bg-gray-100', cardLabel: 'text-gray-500', cardVal: 'text-gray-900', empty: 'text-gray-500 border-gray-300',
    btnA: 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200', btnB: 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200',
    picked: 'bg-emerald-50',
  },
};

export function BillAllocator({ bills, alloc, setAlloc, amount, side, testPrefix = 'alloc', theme = 'dark', unallocated = 0 }) {
  const T = THEMES[theme] || THEMES.dark;
  const [showSettled, setShowSettled] = useState(false);
  const allocated = useMemo(() => Object.values(alloc).reduce((s, v) => s + num(v), 0), [alloc]);
  const totalDue = useMemo(() => bills.reduce((s, b) => s + num(b.outstanding), 0), [bills]);
  const remaining = Math.round((num(amount) - allocated) * 100) / 100;

  const setOne = (bill, v) => setAlloc(a => ({ ...a, [bill.bill_id]: v }));
  const toggle = (bill) => {
    const cur = num(alloc[bill.bill_id]);
    if (cur > 0) { setOne(bill, ''); return; }
    const room = Math.max(0, num(amount) - allocated);
    const take = Math.min(bill.available, room > 0 ? room : bill.available);
    setOne(bill, take > 0 ? String(Math.round(take * 100) / 100) : '');
  };
  const autoFifo = () => {
    let left = num(amount);
    const next = {};
    for (const b of bills) {
      if (left <= 0.005) break;
      const take = Math.min(b.available, left);
      if (take > 0) { next[b.bill_id] = String(Math.round(take * 100) / 100); left -= take; }
    }
    setAlloc(next);
  };
  const clear = () => setAlloc({});
  const over = (b) => num(alloc[b.bill_id]) > b.available + 0.005;

  return (
    <div className="space-y-2" data-testid={`${testPrefix}-grid`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className={`text-[11px] ${T.hint}`}>
          Tick the {side === 'debtor' ? 'invoices' : 'bills'} this money is against, or let it go oldest-first.
          {bills.length > 0 && <span className="ml-1 font-mono" data-testid={`${testPrefix}-total-due`}>· Due across {bills.length} row{bills.length > 1 ? 's' : ''}: {fmt(totalDue)}</span>}
          {unallocated > 0 && <span className="ml-1 font-mono" data-testid={`${testPrefix}-unallocated`}>· on account (not matched to any row): {fmt(unallocated)} → net {fmt(totalDue - unallocated)}</span>}
        </div>
        <div className="flex gap-1.5">
          <button type="button" onClick={autoFifo} data-testid={`${testPrefix}-auto-fifo`}
            className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg border ${T.btnA}`}>
            <Wand2 size={11} /> Auto FIFO
          </button>
          <button type="button" onClick={clear} data-testid={`${testPrefix}-clear`}
            className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded-lg border ${T.btnB}`}>
            <Eraser size={11} /> Clear
          </button>
        </div>
      </div>

      {bills.length === 0 ? (
        <div className={`text-center text-xs py-6 border border-dashed rounded-xl ${T.empty}`}>
          No open {side === 'debtor' ? 'invoices' : 'bills'} — the full amount will sit on account and can be allocated later.
        </div>
      ) : (
        <div className={`border rounded-xl overflow-hidden ${T.box}`}>
          {/* desktop */}
          <table className="w-full text-xs hidden md:table table-fixed">
            <colgroup><col className="w-8" /><col className="w-[30%]" /><col className="w-[16%]" /><col className="w-[17%]" /><col className="w-[17%]" /><col /></colgroup>
            <thead className={T.head}>
              <tr className="text-[10px] uppercase tracking-wider">
                <th className="px-2 py-2" />
                <th className="text-left px-2 py-2 font-medium">{side === 'debtor' ? 'Invoice' : 'Bill'}</th>
                <th className="text-left px-2 py-2 font-medium">Date</th>
                <th className="text-right px-2 py-2 font-medium">Total</th>
                <th className="text-right px-2 py-2 font-medium">Due</th>
                <th className="text-right px-2 py-2 font-medium">Allocate</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${T.divide}`}>
              {bills.map(b => (
                <tr key={b.bill_id} className={`${num(alloc[b.bill_id]) > 0 ? T.picked : ''} ${T.hover}`} data-testid={`${testPrefix}-row-${b.bill_id}`}>
                  <td className="px-2 py-1.5 text-center">
                    <input type="checkbox" checked={num(alloc[b.bill_id]) > 0} onChange={() => toggle(b)} data-testid={`${testPrefix}-check-${b.bill_id}`} className="accent-emerald-500" />
                  </td>
                  <td className="px-2 py-1.5">
                    <div className={`font-mono truncate ${b.kind === 'opening' ? T.refOpening : T.ref}`} title={b.ref}>{b.ref}</div>
                    {b.description && <div className={`text-[10px] truncate ${T.sub}`} title={b.description}>{b.description}</div>}
                    {b.kind === 'opening' && b.paid > 0 && (
                      <div className={`text-[10px] ${T.sub}`} data-testid={`${testPrefix}-opening-paid`}>
                        {fmt(b.paid)} already settled by {(b.settled_by || []).length} receipt{(b.settled_by || []).length === 1 ? '' : 's'} (oldest-first)
                        {(b.settled_by || []).length > 0 && (
                          <button type="button" onClick={() => setShowSettled(s => !s)} className="ml-1 underline" data-testid={`${testPrefix}-opening-toggle`}>{showSettled ? 'hide' : 'show'}</button>
                        )}
                        {showSettled && (
                          <ul className="mt-1 space-y-0.5 font-mono" data-testid={`${testPrefix}-opening-list`}>
                            {b.settled_by.map(s => (
                              <li key={s.id || `${s.date}-${s.amount}`} className={s.orphan ? 'text-red-400' : ''}>
                                {s.date} · {s.ref} · {fmt(s.amount)}{s.orphan ? ' · receipt missing/cancelled' : ''}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </td>
                  <td className={`px-2 py-1.5 font-mono ${T.sub}`}>{b.date || '—'}</td>
                  <td className={`px-2 py-1.5 text-right font-mono ${T.mono}`}>{fmt(b.total)}</td>
                  <td className={`px-2 py-1.5 text-right font-mono ${T.due}`}>{fmt(b.available)}</td>
                  <td className="px-2 py-1.5 text-right">
                    <input type="number" min="0" step="0.01" value={alloc[b.bill_id] ?? ''} onChange={e => setOne(b, e.target.value)}
                      placeholder="0.00" data-testid={`${testPrefix}-amt-${b.bill_id}`}
                      className={`w-full max-w-[120px] border rounded-lg px-2 py-1 text-right font-mono focus:outline-none ${T.input} ${over(b) ? T.over : ''}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* mobile */}
          <div className={`md:hidden divide-y ${T.divide}`}>
            {bills.map(b => (
              <div key={b.bill_id} className={`p-3 flex items-start gap-3 ${num(alloc[b.bill_id]) > 0 ? T.picked : ''}`} data-testid={`${testPrefix}-card-${b.bill_id}`}>
                <input type="checkbox" checked={num(alloc[b.bill_id]) > 0} onChange={() => toggle(b)} className="mt-1 accent-emerald-500" />
                <div className="flex-1 min-w-0">
                  <div className={`font-mono text-xs truncate ${b.kind === 'opening' ? T.refOpening : T.ref}`}>{b.ref}</div>
                  <div className={`text-[10px] flex flex-wrap gap-x-3 ${T.sub}`}>
                    <span>{b.date || '—'}</span><span>Total {fmt(b.total)}</span><span className={T.due}>Due {fmt(b.available)}</span>
                  </div>
                  {b.kind === 'opening' && b.paid > 0 && <div className={`text-[10px] ${T.sub}`}>{fmt(b.paid)} already settled by {(b.settled_by || []).length} receipt(s)</div>}
                </div>
                <input type="number" min="0" step="0.01" value={alloc[b.bill_id] ?? ''} onChange={e => setOne(b, e.target.value)} placeholder="0.00"
                  className={`w-24 border rounded-lg px-2 py-1 text-right font-mono text-xs ${T.input} ${over(b) ? T.over : ''}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2 text-[11px]" data-testid={`${testPrefix}-summary`}>
        <div className={`rounded-lg p-2 ${T.card}`}><div className={T.cardLabel}>Amount</div><div className={`font-mono ${T.cardVal}`}>{fmt(num(amount))}</div></div>
        <div className={`rounded-lg p-2 ${T.card}`}><div className={T.cardLabel}>Allocated</div><div className={`font-mono ${allocated > num(amount) + 0.005 ? 'text-red-500' : 'text-emerald-500'}`}>{fmt(allocated)}</div></div>
        <div className={`rounded-lg p-2 ${T.card}`}><div className={T.cardLabel}>On account</div><div className={`font-mono ${remaining < -0.005 ? 'text-red-500' : 'text-amber-500'}`}>{fmt(Math.max(0, remaining))}</div></div>
      </div>
      {allocated > num(amount) + 0.005 && (
        <div className="text-red-500 text-[11px]" data-testid={`${testPrefix}-over-warning`}>Allocated more than the amount — reduce a bill or raise the amount.</div>
      )}
    </div>
  );
}

export default function SettlementEditor({ open, clientId, clientName, side = 'debtor', voucherId = null, onClose, onSaved, canDelete = false }) {
  const [bills, setBills] = useState([]);
  const [unallocated, setUnallocated] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [voucher, setVoucher] = useState(null);
  const [form, setForm] = useState({ amount: '', date: new Date().toISOString().slice(0, 10), method: 'NEFT', note: '' });
  const [alloc, setAlloc] = useState({});
  const [isNote, setIsNote] = useState(false);
  const isEdit = !!voucherId;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        if (voucherId) {
          const r = await axios.get(`${API}/settlements/${voucherId}`, { withCredentials: true });
          if (cancelled) return;
          const v = r.data.voucher;
          setVoucher(v);
          setIsNote(!!r.data.is_note);
          setBills(r.data.open_bills || []);
          setForm({ amount: String(v.amount ?? ''), date: (v.date || '').slice(0, 10), method: v.payment_mode || 'NEFT', note: v.narration || '' });
          const a = {};
          (r.data.allocations || []).forEach(x => { a[x.bill_id] = String(x.amount); });
          setAlloc(a);
        } else {
          const r = await axios.get(`${API}/settlements/open-bills`, { params: { client_id: clientId, side }, withCredentials: true });
          if (cancelled) return;
          setVoucher(null);
          setIsNote(false);
          setBills(r.data.bills || []);
          setUnallocated(r.data.unallocated || 0);
          setForm({ amount: '', date: new Date().toISOString().slice(0, 10), method: 'NEFT', note: '' });
          setAlloc({});
        }
      } catch (e) { toast.error(e.response?.data?.detail || 'Could not load open bills'); }
      finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [open, clientId, side, voucherId]);

  if (!open) return null;

  const allocations = Object.entries(alloc).filter(([, v]) => num(v) > 0).map(([bill_id, v]) => ({ bill_id, amount: num(v) }));
  const allocated = allocations.reduce((s, a) => s + a.amount, 0);
  const amount = num(form.amount);
  const valid = amount > 0 && allocated <= amount + 0.005 && bills.every(b => num(alloc[b.bill_id]) <= b.available + 0.005);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) { toast.error('Check the amount and allocations'); return; }
    setBusy(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/settlements/${voucherId}`, { amount, date: form.date, method: form.method, note: form.note, allocations }, { withCredentials: true });
        toast.success(isNote ? 'Credit note adjusted against bills · ledger synced' : 'Settlement updated · bills, ledger and cashflow synced');
      } else {
        const r = await axios.post(`${API}/settlements/`, { client_id: clientId, side, amount, date: form.date, method: form.method, note: form.note, allocations }, { withCredentials: true });
        const oa = r.data.on_account || 0;
        toast.success(`${fmt(amount)} ${side === 'debtor' ? 'received' : 'paid'} · ${allocations.length} bill(s)${oa > 0 ? ` · ${fmt(oa)} on account` : ''}`);
      }
      onSaved && (await onSaved());
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm('Delete this receipt/payment? Bills go back to unpaid for this amount and the cashflow row is removed.')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/settlements/${voucherId}`, { withCredentials: true });
      toast.success('Settlement deleted');
      onSaved && (await onSaved());
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
    finally { setBusy(false); }
  };

  const title = isEdit
    ? (isNote ? `Adjust ${side === 'debtor' ? 'Credit Note' : 'Debit Note'} · ${voucher?.voucher_number || ''}`
             : `Edit ${side === 'debtor' ? 'Receipt' : 'Payment'} · ${voucher?.voucher_number || ''}`)
    : (side === 'debtor' ? 'Receive / Advance' : 'Pay / Advance');

  return (
    <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center z-[70] p-0 sm:p-4" onClick={onClose} data-testid="settlement-editor">
      <div className="bg-zinc-900 border border-zinc-700 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between px-4 sm:px-5 py-3.5 border-b border-zinc-800 flex-shrink-0">
          <div className="min-w-0">
            <h3 className="font-heading font-semibold text-zinc-50 text-sm sm:text-base truncate flex items-center gap-2"><Wallet size={15} className="text-emerald-400" /> {title}</h3>
            <p className="text-zinc-500 text-[11px] mt-0.5 truncate">{clientName}{isNote ? ' · pick the invoices this return is adjusted against (amount comes from the goods lines)' : isEdit ? ' · re-allocate or correct this entry' : side === 'debtor' ? ' · pick the invoices this money settles' : ' · pick the bills this money settles'}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200 ml-2" data-testid="settlement-close"><X size={18} /></button>
        </div>

        <form onSubmit={submit} className="p-4 sm:p-5 space-y-3 overflow-y-auto">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            <div className="col-span-2 sm:col-span-1">
              <label className="text-zinc-400 text-xs mb-1 block">Amount (₹) *</label>
              <input required autoFocus={!isNote} readOnly={isNote} disabled={isNote} type="number" step="any" min="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                data-testid="settlement-amount" className={`w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm font-mono focus:outline-none focus:border-emerald-500 ${isNote ? 'opacity-60 cursor-not-allowed' : ''}`} />
            </div>
            <div>
              <label className="text-zinc-400 text-xs mb-1 block">Date</label>
              <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} data-testid="settlement-date"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-emerald-500" />
            </div>
            <div className={isNote ? 'hidden' : ''}>
              <label className="text-zinc-400 text-xs mb-1 block">Mode</label>
              <select value={form.method} onChange={e => setForm(f => ({ ...f, method: e.target.value }))} data-testid="settlement-method"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-emerald-500">
                {[...new Set([form.method, ...METHODS])].filter(Boolean).map(m => <option key={m} value={m} className="bg-zinc-900">{m}</option>)}
              </select>
            </div>
            <div className="col-span-2 sm:col-span-4">
              <label className="text-zinc-400 text-xs mb-1 block">Narration</label>
              <input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="e.g. Cheque 123456 · Sept settlement" data-testid="settlement-note"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-emerald-500" />
            </div>
          </div>

          {loading ? (
            <div className="text-zinc-500 text-xs py-6 text-center animate-pulse">Loading open bills…</div>
          ) : (
            <BillAllocator bills={bills} alloc={alloc} setAlloc={setAlloc} amount={form.amount} side={side} testPrefix="settlement-alloc" unallocated={isEdit ? 0 : unallocated} />
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-2 pt-1">
            {isEdit && canDelete && !isNote && (
              <button type="button" onClick={remove} disabled={busy} data-testid="settlement-delete"
                className="sm:mr-auto py-2.5 px-3 rounded-xl border border-red-800/50 text-red-400 text-xs flex items-center justify-center gap-1.5 hover:bg-red-900/20">
                <Trash2 size={12} /> Delete entry
              </button>
            )}
            <button type="button" onClick={onClose} className="py-2.5 px-4 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
            <button type="submit" disabled={busy || !valid} data-testid="settlement-save"
              className="py-2.5 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-1.5">
              <Save size={13} /> {busy ? 'Saving…' : isEdit ? 'Save changes' : side === 'debtor' ? 'Record receipt' : 'Record payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
